// Per-carrier route history hook (D3h). Composed by LogisticsCarriersList
// when a row's "Routes" toggle expands -- fetches the carrier's routes
// (filtered server-side via the carrier_id param the routes-list
// endpoint already supports since D3b) plus the units list (once on
// mount, cached for the lifetime of the carriers panel) so the rendered
// sub-card can show unit display labels instead of bare UUIDs.
//
// Sits beside useLogisticsCarriersPanel rather than expanding it: the
// route-history affordance is orthogonal to the carriers list / create /
// edit / toggle-active concerns the main hook already owns. Same
// single-hook-per-affordance shape as the D3g UI per-stop edits split
// (useLogisticsRouteStopEdits sibling to useLogisticsRouteDetail).
//
// Pattern note: units are fetched once on first mount and never
// refetched. The pilot scale (small unit counts per org) makes this
// a no-op for performance, and any unit added during a long-lived
// carriers-panel session still renders by UUID-short fallback via
// unitLabel(). If volume ever matters, hoist units to a parent hook
// and pass them down.
import { useCallback, useEffect, useState } from "react";
import { listLogisticsRoutes, listLogisticsUnits, type LogiRoute } from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturing/types";

// Sort routes for display: newest delivery activity first.
// Prefer completed_at (terminal stop) > scheduled_for (planned out)
// > created_at (entry order). Nulls sink within each tier.
function compareRoutes(a: LogiRoute, b: LogiRoute): number {
  const ac = a.completed_at;
  const bc = b.completed_at;
  if (ac && bc) return bc.localeCompare(ac);
  if (ac && !bc) return -1;
  if (!ac && bc) return 1;
  const as = a.scheduled_for;
  const bs = b.scheduled_for;
  if (as && bs) return bs.localeCompare(as);
  if (as && !bs) return -1;
  if (!as && bs) return 1;
  return b.created_at.localeCompare(a.created_at);
}

export function useLogisticsCarrierRouteHistory(expandedCarrierId: string | null) {
  const [routes, setRoutes] = useState<LogiRoute[]>([]);
  const [units, setUnits] = useState<MfgUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Units fetch on mount only -- treated as a stable lookup table for
  // the panel's lifetime. Non-fatal: if the fetch fails, unit_id UUID
  // short prefixes render as the label via the unitLabel() fallback.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await listLogisticsUnits();
      if (cancelled) return;
      if (res.ok) {
        setUnits(Array.isArray(res.data.units) ? res.data.units : []);
      } else {
        setUnits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!expandedCarrierId) {
      setRoutes([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await listLogisticsRoutes({
      carrier_id: expandedCarrierId,
    });
    setLoading(false);
    if (!res.ok) {
      setRoutes([]);
      setError(res.error);
      return;
    }
    const list = Array.isArray(res.data.routes) ? res.data.routes : [];
    setRoutes([...list].sort(compareRoutes));
  }, [expandedCarrierId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { routes, units, loading, error, reload };
}
